import { Selector } from "@rhumbix/rmbx_design_system_web"
import { useImperativeHandle, useState, forwardRef, FunctionComponent, useEffect, useRef } from "react"
import { tSelectorOption } from "../../../SelectorV3/types"
import { ICellEditorParams } from "ag-grid-community"
import { colorStyles } from "../../../SelectorV3/styles"
import { getFlagEnabled } from "../../../getFlagValue"

type tProps = ICellEditorParams & {
    value: string
    default?: string
    disableOptions: string[]
    placeholder?: string
    values: string[]
    rowIndex: number
    fixedOptions: string[]
}

/**
 * A single-choice cell editor for AG-Grid that renders a searchable dropdown selector.
 *
 * Supports an optional `default` value that is treated as "no selection" — when the
 * current value matches the default, the selector shows a placeholder instead.
 * Clearing the selector returns the default value rather than undefined.
 */
const SingleChoiceCellEditor: FunctionComponent<tProps> = forwardRef((props: tProps, ref) => {
    const defaultValue = props.default
    const isDefaultValue = (val: string | undefined): boolean => defaultValue != null && val === defaultValue
    const initialValue = getFlagEnabled("WA-9079-wage-rates")
        ? isDefaultValue(props.value)
            ? undefined
            : props.value
        : props.value

    const [currentValue, setCurrentValue] = useState(initialValue)
    const [searchValue, setSearchValue] = useState("")
    const [currentOptions, setCurrentOptions] = useState(props.values.map(ea => ({ label: ea, value: ea })))
    const [previousValue, setPreviousValue] = useState(initialValue)

    // This is required to have the cell editor pop out of the cell when the contents are too large for the cell
    useImperativeHandle(ref, () => {
        return {
            getValue: () => (getFlagEnabled("WA-9079-wage-rates") ? currentValue ?? defaultValue : currentValue),
        }
    })
    const refInput = useRef<HTMLDivElement>(null)

    useEffect(() => {
        refInput?.current?.focus()
    }, [])

    useEffect(() => {
        if (previousValue !== currentValue) {
            props.api.stopEditing()
            props.api.setFocusedCell(props.rowIndex, props.column.getColId())
            setPreviousValue(currentValue)
        }
    }, [currentValue])

    const handleChange = (selectedValue: tSelectorOption | null) => {
        setCurrentValue(selectedValue?.value)
    }
    const styles = {
        ...colorStyles,
        indicatorSeparator: (base: React.CSSProperties) => {
            return {
                ...base,
                marginTop: 8,
                marginBottom: 8,
            }
        },
        input: (base: React.CSSProperties) => ({
            ...base,
        }),
    }

    const handleInputChange = (value: string) => {
        setSearchValue(value)
        setCurrentOptions(
            props.values
                .filter(ea => ea.toLowerCase().includes(value.toLowerCase()))
                .map(ea => ({ label: ea, value: ea }))
        )
    }

    return (
        <div className="single-choice-editor" id={`single-choice-editor-${props.rowIndex}`}>
            <Selector
                isClearable={true}
                innerRef={refInput}
                inputValue={searchValue}
                isMulti={false}
                isDisabled={false}
                onChange={handleChange}
                onInputChange={handleInputChange}
                openMenuOnFocus={true}
                options={currentOptions}
                initialSelectedOption={
                    getFlagEnabled("WA-9079-wage-rates")
                        ? currentValue != null
                            ? { label: currentValue, value: currentValue }
                            : undefined
                        : { label: currentValue, value: currentValue }
                }
                placeholder={props.placeholder}
                styles={styles}
            />
        </div>
    )
})

export default SingleChoiceCellEditor
